import { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Calendar, MapPin, Minus, Plus, ShoppingCart, ChevronDown, Filter, X, Ticket as TicketIcon, Package as PackageIcon, Pencil } from "lucide-react";
import { useAuth, API } from "../../context/AuthContext";
import { useCart } from "../../context/CartContext";
import ShopHeader from "./ShopHeader";
import { shopStatus, StatusBand, StatusPill, FADE_IMG, FADE_BODY } from "./ShopStatusBand";
import { groupByCategory } from "../shop_admin/ShopAdminDashboard";
import { isBuyer } from "../../components/CartPricePreview";

export default function PublicEventDetail() {
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  // iter163: category accordion + filter state.
  // collapsedCats holds the group-keys the user has explicitly collapsed —
  // everything expanded by default so a first-time shopper doesn't need to
  // hunt for the "open" button on a small event.
  const [collapsedCats, setCollapsedCats] = useState(() => new Set());
  // selectedCats: if empty, show all groups. Otherwise, only show groups
  // whose key is in the set. Applies to BOTH tiers and packages using the
  // same category label so a "Weekend Pass" chip hides both.
  const [selectedCats, setSelectedCats] = useState(() => new Set());
  const [filterOpen, setFilterOpen] = useState(false);
  // iter168: second filter — scope toggle. "all" shows both sections,
  // "tickets" or "packages" hides the other and scrolls to the visible one.
  // Only rendered when the event actually HAS both sections so a
  // ticket-only or package-only event doesn't get useless controls.
  const [selectedScope, setSelectedScope] = useState("all");
  const tiersRef = useRef(null);
  const packagesRef = useRef(null);
  const navigate = useNavigate();
  const { tenantSlug, eventId } = useParams();
  const { user, token: userToken } = useAuth();
  const { setTicketQty, setPackageQty, getTicketQty, getPackageQty, getPackageEntry, count, open } = useCart();

  useEffect(() => {
    // Include the bearer token (when present) so the backend can filter tiers/packages
    // by the logged-in user's audience. Without this, members see the anonymous view.
    const headers = userToken ? { Authorization: `Bearer ${userToken}` } : {};
    axios.get(`${API}/ticketing/public/events/${eventId}`, { headers })
      .then(r => setEvent(r.data))
      .catch(() => toast.error("Event not found"))
      .finally(() => setLoading(false));
  }, [eventId, userToken]);

  // Cart mutation helpers scoped to this event.
  const updateTierQty = (t, variantKey, delta) => {
    const variantId = variantKey === "_" ? null : variantKey;
    const variant = variantId ? (t.variants || []).find(v => v.id === variantId) : null;
    const base = priceOfVariant(t, variantId);
    const current = getTicketQty(eventId, t.id, variantId);
    setTicketQty({
      event_id: eventId,
      event_name: event.name,
      ticket_type_id: t.id,
      ticket_type_name: t.name,
      variant_id: variantId,
      variant_name: variant?.name || null,
      base_unit_price: base,
    }, current + delta);
  };
  const updatePkgQty = (p, newQty, copies) => {
    // copies: Array<Array<{tier_id, variant_id, variant_name}>>
    // Compute average base_unit_price for display in the cart overlay.
    let base = 0;
    if (copies && copies.length > 0) {
      for (const copy of copies) {
        let copyBase = 0;
        for (const sel of copy) {
          const tier = (p.tiers || []).find(t => t.id === sel.tier_id);
          if (!tier) continue;
          const variants = tier.variants || [];
          let vRaw = Number(tier.price || 0);
          if (variants.length > 0 && sel.variant_id) {
            const v = variants.find(x => x.id === sel.variant_id);
            if (v?.price_override != null) vRaw = Number(v.price_override);
          }
          const td = tier.discount;
          let tierEff = vRaw;
          if (td && td.value > 0) {
            if (td.type === "percent") tierEff = Math.max(0, Math.round(vRaw * (1 - td.value / 100) * 100) / 100);
            else if (td.type === "fixed") tierEff = Math.max(0, Math.round((vRaw - td.value) * 100) / 100);
          }
          copyBase += tierEff;
        }
        const pd = p.discount;
        let copyEff = copyBase;
        if (pd && pd.value > 0) {
          if (pd.type === "percent") copyEff = Math.max(0, Math.round(copyBase * (1 - pd.value / 100) * 100) / 100);
          else if (pd.type === "fixed") copyEff = Math.max(0, Math.round((copyBase - pd.value) * 100) / 100);
        }
        base += copyEff;
      }
      base = newQty > 0 ? Math.round(base / newQty * 100) / 100 : 0;
    } else {
      base = Number(p.effective_price ?? p.base_price ?? 0);
    }
    setPackageQty({
      event_id: eventId,
      event_name: event.name,
      package_id: p.id,
      package_name: p.name,
      base_unit_price: base,
      variant_selections: (copies || []).map(copy =>
        copy.map(s => ({ tier_id: s.tier_id, variant_id: s.variant_id || null }))
      ),
      variant_names: (copies || []).map(copy =>
        copy.map(s => s.variant_name || null)
      ),
    }, newQty);
  };

  const priceOfTier = (t) => Number((typeof t?.effective_price === "number") ? t.effective_price : (t?.price || 0));
  const priceOfVariant = (t, vid) => {
    if (!t) return 0;
    if (!vid) return priceOfTier(t);
    const v = (t.variants || []).find(x => x.id === vid);
    const raw = v?.price_override != null ? Number(v.price_override) : Number(t.price || 0);
    if (t.discount && t.discount.value > 0) {
      if (t.discount.type === "percent") return Math.max(0, Math.round(raw * (1 - t.discount.value / 100) * 100) / 100);
      if (t.discount.type === "fixed") return Math.max(0, Math.round((raw - t.discount.value) * 100) / 100);
    }
    return raw;
  };

  const openCart = () => open();

  // iter163: build the union of category labels across tiers + packages so
  // the filter chip bar shows every category exactly once, ordered by the
  // grouping helper's own effective order (min category_sort_order, then
  // label). Uncategorized items collapse into a single "Other" chip.
  const categoryOptions = useMemo(() => {
    if (!event) return [];
    const seen = new Map();
    const push = (g) => { if (!seen.has(g.key)) seen.set(g.key, { key: g.key, label: g.label, order: g.order }); };
    groupByCategory(event.ticket_types || []).forEach(push);
    groupByCategory(event.packages || []).forEach(push);
    return [...seen.values()].sort((a, b) => a.order - b.order || a.label.localeCompare(b.label));
  }, [event]);

  const toggleCollapse = (key) => setCollapsedCats(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const toggleFilter = (key) => setSelectedCats(prev => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const clearFilters = () => setSelectedCats(new Set());
  // Only apply filtering when the user has picked at least one chip.
  const shouldShow = (key) => selectedCats.size === 0 || selectedCats.has(key);

  if (loading) return <div className="p-10 text-center text-slate-400">Loading...</div>;
  if (!event) return <div className="p-10 text-center text-slate-400">Event not found</div>;

  const tierGroups = groupByCategory(event.ticket_types || []).filter(g => shouldShow(g.key));
  const pkgGroups = groupByCategory(event.packages || []).filter(g => shouldShow(g.key));
  const hasTickets = (event.ticket_types || []).length > 0;
  const hasPackages = (event.packages || []).length > 0;
  const showTiersSection = selectedScope !== "packages" && tierGroups.length > 0;
  const showPkgsSection = selectedScope !== "tickets" && pkgGroups.length > 0;
  const nothingMatches = selectedCats.size > 0 && tierGroups.length === 0 && pkgGroups.length === 0;

  // iter168: switch scope + scroll to the newly visible section. Smooth
  // scroll and a small delay so the DOM has painted the (un)hidden
  // section before we measure its top.
  const jumpTo = (scope) => {
    setSelectedScope(scope);
    setTimeout(() => {
      const target = scope === "packages" ? packagesRef.current : tiersRef.current;
      if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 60);
  };

  return (
    <div className="min-h-screen bg-slate-50 pb-24" data-testid="public-event-detail">
      <ShopHeader tenantSlug={tenantSlug} backTo={`/${tenantSlug}`} backLabel="Back to shop" />

      <main className="max-w-3xl mx-auto px-4 py-6">
        {event.marketing_banner_url && (
          <div className="rounded-3xl overflow-hidden mb-5 relative bg-slate-50" data-testid="event-banner">
            <img src={event.marketing_banner_url} alt="" className="w-full h-auto max-h-[420px] block mx-auto" />
            {/* Desktop overlay — hidden on mobile so it can't collide with the
                image. Mobile gets a solid card underneath (below), which
                stays readable at any screen width. */}
            <div className="hidden sm:flex absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex-col justify-end p-6 pointer-events-none">
              <h2 className="text-white text-3xl font-extrabold">{event.name}</h2>
              <p className="text-white/80 mt-1 text-sm flex items-center gap-3 flex-wrap">
                <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />
                  {event.start_date}{event.end_date && event.end_date !== event.start_date ? ` – ${event.end_date}` : ""}
                </span>
                {event.location && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {event.location}</span>}
              </p>
            </div>
          </div>
        )}

        {/* Mobile title block — always shown when a banner is present,
            hidden on sm+ where the overlay handles it. */}
        {event.marketing_banner_url && (
          <div className="sm:hidden bg-white rounded-2xl p-4 mb-5 shadow-sm -mt-3">
            <h2 className="text-xl font-extrabold text-[#1e3a5f] mb-2">{event.name}</h2>
            <div className="flex items-center gap-3 text-sm text-slate-500 flex-wrap">
              <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />
                {event.start_date}{event.end_date && event.end_date !== event.start_date ? ` – ${event.end_date}` : ""}
              </span>
              {event.location && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {event.location}</span>}
            </div>
          </div>
        )}

        {!event.marketing_banner_url && (
          <div className="bg-white rounded-2xl p-5 mb-5 shadow-sm">
            <h2 className="text-xl font-extrabold text-[#1e3a5f] mb-2">{event.name}</h2>
            <div className="flex items-center gap-4 text-sm text-slate-500 mb-3 flex-wrap">
              <span className="flex items-center gap-1"><Calendar className="w-4 h-4" />
                {event.start_date}{event.end_date && event.end_date !== event.start_date ? ` – ${event.end_date}` : ""}
              </span>
              {event.location && <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {event.location}</span>}
            </div>
          </div>
        )}
        {event.marketing_description && (
          <p className="text-slate-600 text-sm leading-relaxed whitespace-pre-wrap mb-5">{event.marketing_description}</p>
        )}

        {(hasTickets || hasPackages) && (
          <div className="flex items-start gap-2 mb-5 px-4 py-3 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs sm:text-sm" data-testid="event-pricing-notice">
            <TicketIcon className="w-4 h-4 mt-0.5 shrink-0" />
            <p>
              Prices shown are base prices. Your final discounted prices (member, family and guest rates) are calculated from your login
              {isBuyer(user) ? " and shown in your cart and at checkout." : " — log in to see them in your cart, or verify your email at checkout."}
            </p>
          </div>
        )}

        {/* iter163/168: category filter + scope filter — both live top-right
            after the banner. Category filter is only shown when the event
            has >1 category; scope filter is only shown when the event
            has BOTH tickets and packages. */}
        {(categoryOptions.length > 1 || (hasTickets && hasPackages)) && (
          <div className="flex items-start justify-end gap-2 mb-4 flex-wrap" data-testid="event-filter-bar">
            {hasTickets && hasPackages && (
              <div className="inline-flex bg-white border border-slate-200 rounded-full p-0.5 shadow-sm" data-testid="event-scope-filter">
                <button
                  onClick={() => setSelectedScope("all")}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${selectedScope === "all" ? "bg-[#1e3a5f] text-white" : "text-slate-600 hover:bg-slate-100"}`}
                  data-testid="event-scope-all"
                >
                  All
                </button>
                <button
                  onClick={() => jumpTo("tickets")}
                  className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors ${selectedScope === "tickets" ? "bg-[#1e3a5f] text-white" : "text-slate-600 hover:bg-slate-100"}`}
                  data-testid="event-scope-tickets"
                >
                  <TicketIcon className="w-3 h-3" /> Tickets
                </button>
                <button
                  onClick={() => jumpTo("packages")}
                  className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium transition-colors ${selectedScope === "packages" ? "bg-[#1e3a5f] text-white" : "text-slate-600 hover:bg-slate-100"}`}
                  data-testid="event-scope-packages"
                >
                  <PackageIcon className="w-3 h-3" /> Packages
                </button>
              </div>
            )}
            {categoryOptions.length > 1 && (
            <div className="relative">
              <button
                onClick={() => setFilterOpen(v => !v)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${selectedCats.size > 0 ? "bg-[#1e3a5f] text-white border-[#1e3a5f]" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}
                data-testid="event-filter-toggle"
              >
                <Filter className="w-3.5 h-3.5" />
                Filter{selectedCats.size > 0 ? ` (${selectedCats.size})` : ""}
                <ChevronDown className={`w-3.5 h-3.5 transition-transform ${filterOpen ? "rotate-180" : ""}`} />
              </button>
              {filterOpen && (
                <div className="absolute right-0 mt-2 w-64 bg-white rounded-2xl border border-slate-200 shadow-lg p-3 z-20" data-testid="event-filter-panel">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs font-bold text-slate-700 uppercase tracking-wider">Categories</p>
                    {selectedCats.size > 0 && (
                      <button onClick={clearFilters} className="text-[10px] text-[#0ea5e9] font-medium hover:underline" data-testid="event-filter-clear">Clear</button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5">
                    {categoryOptions.map(opt => {
                      const active = selectedCats.has(opt.key);
                      return (
                        <button
                          key={opt.key}
                          onClick={() => toggleFilter(opt.key)}
                          className={`px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${active ? "bg-[#0ea5e9] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}
                          data-testid={`event-filter-chip-${opt.key}`}
                        >
                          {opt.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            )}
          </div>
        )}

        {nothingMatches && (
          <div className="bg-white rounded-2xl p-8 text-center shadow-sm mb-8" data-testid="event-no-matches">
            <p className="text-slate-500 text-sm font-medium">No tickets match your filters</p>
            <button onClick={clearFilters} className="mt-3 text-xs text-[#0ea5e9] font-bold hover:underline" data-testid="event-no-matches-clear">Clear filters</button>
          </div>
        )}

        {!nothingMatches && showTiersSection && (
          <div ref={tiersRef}>
            <h3 className="text-2xl font-extrabold text-[#1e3a5f] mb-4">Tickets</h3>
            {tierGroups.map(group => {
              const collapsed = collapsedCats.has(group.key);
              return (
              <div key={group.key} className="mb-4" data-testid={`public-tier-group-${group.key}`}>
                <button
                  onClick={() => toggleCollapse(group.key)}
                  className="w-full flex items-center justify-between gap-2 mb-3 px-1 group"
                  data-testid={`public-tier-group-toggle-${group.key}`}
                  aria-expanded={!collapsed}
                >
                  <h4 className="text-sm font-bold uppercase tracking-wider text-slate-500 group-hover:text-[#1e3a5f] transition-colors" data-testid={`public-tier-group-label-${group.key}`}>
                    {group.label}
                    <span className="ml-2 text-[10px] font-normal text-slate-400 normal-case">{group.items.length}</span>
                  </h4>
                  <ChevronDown className={`w-4 h-4 text-slate-400 group-hover:text-[#1e3a5f] transition-transform ${collapsed ? "-rotate-90" : ""}`} />
                </button>
                {!collapsed && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {group.items.map(t => {
            const hasVariants = (t.variants || []).length > 0;
            const showDiscountBadge = t.discount && t.discount.value > 0;
            const st = shopStatus(t);
            const blocked = !!st?.blocked;
            return (
              <div key={t.id} className={`relative bg-white rounded-2xl overflow-hidden shadow-sm flex flex-col ${blocked ? "ring-1 ring-slate-200" : ""}`} data-testid={`ticket-tier-${t.id}`} data-sold-out={t.sold_out ? "true" : "false"} data-status={st?.kind || "open"}>
                <div className="relative w-full aspect-square bg-slate-100 overflow-hidden">
                  <div className={`w-full h-full ${blocked ? FADE_IMG : ""}`}>
                  {t.banner_url ? (
                    <img src={t.banner_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-300 text-5xl">🎟️</div>
                  )}
                  </div>
                  <StatusBand status={st} testid={`ticket-tier-status-${t.id}`} />
                  {showDiscountBadge && !blocked && <span className="absolute top-2 right-2 text-[10px] bg-amber-400 text-amber-900 font-bold px-2 py-0.5 rounded-full">
                    {t.discount.type === "percent" ? `-${t.discount.value}%` : `-€${t.discount.value}`}
                  </span>}
                </div>
                <div className={`p-4 flex-1 flex flex-col ${blocked ? FADE_BODY : ""}`} aria-disabled={blocked || undefined}>
                  <h4 className="font-bold text-[#1e3a5f]">{t.name}</h4>
                  {t.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{t.description}</p>}
                  {(() => {
                    const base = Number(t.price || 0);
                    const effective = typeof t.effective_price === "number" ? t.effective_price : base;
                    const showStrike = effective < base;
                    return (
                      <div className="mt-2">
                        <div className="flex items-baseline gap-2 flex-wrap">
                          {showStrike && (
                            <span className="text-xs text-slate-400 line-through" data-testid={`ticket-strike-price-${t.id}`}>€{base.toFixed(2)}</span>
                          )}
                          <span className="text-xl font-extrabold text-[#2d8a4e]" data-testid={`ticket-base-price-${t.id}`}>€{effective.toFixed(2)}</span>
                        </div>
                        <p className="text-[11px] text-slate-500 mt-0.5" data-testid={`ticket-final-price-note-${t.id}`}>final price is available at check out</p>
                      </div>
                    );
                  })()}
                  {/* Ticket-count exposure removed for end-users (2026-02). */}
                  {blocked ? <StatusPill status={st} testid={`ticket-tier-pill-${t.id}`} /> : hasVariants ? (
                    <div className="mt-3 space-y-2">
                      <p className="text-xs text-slate-500">Variants:</p>
                      {t.variants.map(v => {
                        const vRaw = v?.price_override != null ? Number(v.price_override) : Number(t.price);
                        // Apply tier-level discount to variant price (mirrors backend).
                        let vBase = vRaw;
                        if (t.discount && t.discount.value > 0) {
                          if (t.discount.type === "percent") vBase = Math.max(0, Math.round(vRaw * (1 - t.discount.value / 100) * 100) / 100);
                          else if (t.discount.type === "fixed") vBase = Math.max(0, Math.round((vRaw - t.discount.value) * 100) / 100);
                        }
                        const qty = getTicketQty(eventId, t.id, v.id);
                        return (
                          <div key={v.id} className="flex items-center justify-between gap-2 bg-slate-50 rounded-lg px-2 py-1.5" data-testid={`variant-row-${t.id}-${v.id}`}>
                            <div className="flex-1 min-w-0">
                              <p className="text-xs font-medium text-slate-700 truncate">{v.name}</p>
                              <p className="text-[11px] text-[#2d8a4e] font-bold">€{Number(vBase || 0).toFixed(2)}</p>
                            </div>
                            <button
                              onClick={() => updateTierQty(t, v.id, -1)}
                              disabled={!t.available || !qty}
                              className="w-7 h-7 rounded-full bg-white border border-slate-200 hover:bg-slate-100 disabled:opacity-40 flex items-center justify-center"
                              data-testid={`qty-minus-${t.id}-${v.id}`}
                            ><Minus className="w-3 h-3" /></button>
                            <span className="w-5 text-center text-xs font-bold" data-testid={`qty-${t.id}-${v.id}`}>{qty}</span>
                            <button
                              onClick={() => updateTierQty(t, v.id, +1)}
                              disabled={!t.available}
                              className="w-7 h-7 rounded-full bg-[#1e3a5f] text-white hover:bg-[#15294a] disabled:opacity-40 flex items-center justify-center"
                              data-testid={`qty-plus-${t.id}-${v.id}`}
                            ><Plus className="w-3 h-3" /></button>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="flex items-center justify-end gap-2 mt-auto pt-3">
                      <button
                        onClick={() => updateTierQty(t, "_", -1)}
                        disabled={!t.available || !getTicketQty(eventId, t.id, null)}
                        className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 disabled:opacity-40 flex items-center justify-center"
                        data-testid={`qty-minus-${t.id}`}
                      ><Minus className="w-4 h-4" /></button>
                      <span className="w-6 text-center font-bold" data-testid={`qty-${t.id}`}>{getTicketQty(eventId, t.id, null)}</span>
                      <button
                        onClick={() => updateTierQty(t, "_", +1)}
                        disabled={!t.available}
                        className="w-8 h-8 rounded-full bg-[#1e3a5f] text-white hover:bg-[#15294a] disabled:opacity-40 flex items-center justify-center"
                        data-testid={`qty-plus-${t.id}`}
                      ><Plus className="w-4 h-4" /></button>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
                </div>
                )}
              </div>
              );
            })}
          </div>
        )}

        {!nothingMatches && showPkgsSection && (
          <div ref={packagesRef}>
            <h3 className="text-2xl font-extrabold text-[#1e3a5f] mb-4">Packages</h3>
            {pkgGroups.map(group => {
              const collapsed = collapsedCats.has(`pkg-${group.key}`);
              const toggleKey = `pkg-${group.key}`;
              return (
              <div key={group.key} className="mb-4" data-testid={`public-package-group-${group.key}`}>
                <button
                  onClick={() => toggleCollapse(toggleKey)}
                  className="w-full flex items-center justify-between gap-2 mb-3 px-1 group"
                  data-testid={`public-package-group-toggle-${group.key}`}
                  aria-expanded={!collapsed}
                >
                  <h4 className="text-sm font-bold uppercase tracking-wider text-slate-500 group-hover:text-[#1e3a5f] transition-colors" data-testid={`public-package-group-label-${group.key}`}>
                    {group.label}
                    <span className="ml-2 text-[10px] font-normal text-slate-400 normal-case">{group.items.length}</span>
                  </h4>
                  <ChevronDown className={`w-4 h-4 text-slate-400 group-hover:text-[#1e3a5f] transition-transform ${collapsed ? "-rotate-90" : ""}`} />
                </button>
                {!collapsed && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {group.items.map(p => (
                    <PackageCard
                      key={p.id}
                      pkg={p}
                      eventId={eventId}
                      currentQty={getPackageQty(eventId, p.id)}
                      cartEntry={getPackageEntry(eventId, p.id)}
                      onChange={(newQty, copies) => updatePkgQty(p, newQty, copies)}
                    />
                  ))}
                </div>
                )}
              </div>
              );
            })}
          </div>
        )}
      </main>

      {count > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 shadow-lg">
          <div className="max-w-3xl mx-auto flex items-center justify-between gap-4">
            <div>
              <p className="text-xs text-slate-500">{count} item{count !== 1 ? "s" : ""} in cart</p>
              <p className="text-[10px] text-slate-400">Tap to review and check out</p>
            </div>
            <button
              onClick={openCart}
              className="flex-1 max-w-xs py-3 rounded-full bg-[#2d8a4e] text-white font-bold flex items-center justify-center gap-2"
              data-testid="shop-open-cart-btn"
            >
              <ShoppingCart className="w-4 h-4" /> View cart
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// PackageCard: renders a package tile with per-COPY per-tier variant selectors.
// Each copy = one purchased package instance and shows one dropdown per tier
// occurrence in the package. Users can add/remove copies via +/- buttons.
// Recomputes the effective per-copy prices live and pushes the full
// variant_selections array into the cart entry.
function PackageCard({ pkg, eventId, currentQty, cartEntry, onChange }) {
  const tiers = pkg.tiers || [];
  const anyTierHasVariants = tiers.some(t => (t.variants || []).length > 0);

  const _defaultCopy = () => tiers.map(t => {
    const variants = t.variants || [];
    const vid = variants.length > 0 ? variants[0].id : null;
    return { tier_id: t.id, variant_id: vid, variant_name: variants.length > 0 ? variants[0].name : null };
  });

  // iter189: rehydrate a single copy from the cart's persisted arrays.
  // Falls back to defaults for any missing tier or variant so a stale cart
  // (e.g. a variant removed by an admin) still renders sanely.
  const _copyFromCart = (occIdx, vsel, vname) => tiers.map((t, i) => {
    const variants = t.variants || [];
    const savedSel = Array.isArray(vsel) && Array.isArray(vsel[occIdx]) ? vsel[occIdx][i] : null;
    const savedName = Array.isArray(vname) && Array.isArray(vname[occIdx]) ? vname[occIdx][i] : null;
    const savedVid = savedSel && typeof savedSel === "object" ? savedSel.variant_id : null;
    const matched = savedVid ? variants.find(v => v.id === savedVid) : null;
    const fallback = variants[0] || null;
    const chosen = matched || fallback;
    return {
      tier_id: t.id,
      variant_id: chosen?.id ?? null,
      variant_name: chosen?.name ?? (savedName || null),
    };
  });

  // `copies` is the authoritative source of the cart entry for this package.
  // Initialize from the cart's persisted variant_selections when available
  // so returning from checkout preserves the shopper's per-attendee picks.
  const [copies, setCopies] = useState(() => {
    const vsel = cartEntry?.variant_selections;
    const vname = cartEntry?.variant_names;
    return Array.from({ length: currentQty }, (_, i) => _copyFromCart(i, vsel, vname));
  });

  // iter189: keep local copies in sync with cart-level changes. Two cases:
  //   1. `currentQty` diverges from `copies.length` — pad from cart's stored
  //      selections (or defaults as a last resort), or truncate.
  //   2. `cartEntry` identity changes (server merge, cross-tab sync) — refresh
  //      each existing copy from the cart's variant_selections so a stale
  //      first-render doesn't linger. Only touches copies when the stored
  //      selection actually differs so we don't stomp an in-progress edit.
  useEffect(() => {
    const vsel = cartEntry?.variant_selections;
    const vname = cartEntry?.variant_names;
    setCopies(prev => {
      let next = prev;
      if (currentQty !== prev.length) {
        if (currentQty > prev.length) {
          const extra = Array.from({ length: currentQty - prev.length },
            (_, i) => _copyFromCart(prev.length + i, vsel, vname));
          next = [...prev, ...extra];
        } else {
          next = prev.slice(0, currentQty);
        }
      }
      if (cartEntry && Array.isArray(vsel)) {
        let changed = false;
        const refreshed = next.map((copy, i) => {
          const target = _copyFromCart(i, vsel, vname);
          const same = copy.length === target.length &&
            copy.every((c, j) => c.variant_id === target[j].variant_id);
          if (!same) { changed = true; return target; }
          return copy;
        });
        if (changed) next = refreshed;
      }
      return next === prev ? prev : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentQty, cartEntry]);

  const isAvailable = pkg.available !== false;
  const reason = pkg.unavailable_reason || "";
  const isSoldOut = /sold out/i.test(reason);
  const st = shopStatus(pkg);
  const blocked = !!st?.blocked;

  // Compute total (post pkg discount) and static base (no variant, no discount) for strikethrough.
  const { copyEffectivePrices, staticBase } = useMemo(() => {
    const prices = copies.map(copy => {
      let copyBase = 0;
      for (const sel of copy) {
        const tier = tiers.find(t => t.id === sel.tier_id);
        if (!tier) continue;
        const variants = tier.variants || [];
        let vRaw = Number(tier.price || 0);
        if (variants.length > 0 && sel.variant_id) {
          const v = variants.find(x => x.id === sel.variant_id);
          if (v?.price_override != null) vRaw = Number(v.price_override);
        }
        const td = tier.discount;
        let tierEff = vRaw;
        if (td && td.value > 0) {
          if (td.type === "percent") tierEff = Math.max(0, Math.round(vRaw * (1 - td.value / 100) * 100) / 100);
          else if (td.type === "fixed") tierEff = Math.max(0, Math.round((vRaw - td.value) * 100) / 100);
        }
        copyBase += tierEff;
      }
      const pd = pkg.discount;
      let eff = copyBase;
      if (pd && pd.value > 0) {
        if (pd.type === "percent") eff = Math.max(0, Math.round(copyBase * (1 - pd.value / 100) * 100) / 100);
        else if (pd.type === "fixed") eff = Math.max(0, Math.round((copyBase - pd.value) * 100) / 100);
      }
      return { base: Math.round(copyBase * 100) / 100, effective: Math.round(eff * 100) / 100 };
    });
    return { copyEffectivePrices: prices, staticBase: Number(pkg.base_price || 0) };
  }, [copies, tiers, pkg]);

  const displayUnitPrice = copies.length > 0
    ? Math.round(copyEffectivePrices.reduce((s, p) => s + p.effective, 0) / copies.length * 100) / 100
    : Number(pkg.effective_price ?? 0);
  const showStrike = staticBase > 0 && displayUnitPrice < staticBase;

  // iter185: variant picking now happens in a modal (same UX as adding an
  // attendee from the cart) so the product details stay visible on the
  // card. `newCopy` holds the modal's in-progress selection.
  // iter186: modal also supports editing an existing attendee — when
  // `editingIdx` is a number, save REPLACES the copy at that index
  // instead of appending a new one.
  const [adding, setAdding] = useState(false);
  const [editingIdx, setEditingIdx] = useState(null);
  const [newCopy, setNewCopy] = useState([]);
  const setNewCopyVariant = (occIdx, variantId) => {
    const tier = tiers[occIdx];
    const v = (tier?.variants || []).find(x => x.id === variantId);
    setNewCopy(prev => prev.map((sel, i) => i === occIdx
      ? { tier_id: sel.tier_id, variant_id: variantId, variant_name: v?.name || null }
      : sel));
  };
  const openEditAttendee = (idx) => {
    const existing = copies[idx];
    if (!existing) return;
    // Rehydrate from stored selection, filling any missing variant_name
    // from the tier's variant list so the label stays accurate after a
    // reload from cart state.
    const rehydrated = tiers.map((t, occIdx) => {
      const sel = existing[occIdx] || {};
      const variants = t.variants || [];
      const v = variants.find(x => x.id === sel.variant_id) || variants[0];
      return {
        tier_id: t.id,
        variant_id: v?.id ?? null,
        variant_name: v?.name ?? null,
      };
    });
    setNewCopy(rehydrated);
    setEditingIdx(idx);
    setAdding(true);
  };

  const commit = (nextCopies) => {
    setCopies(nextCopies);
    onChange(nextCopies.length, nextCopies);
  };
  const handlePlus = () => {
    if (!isAvailable) return;
    // No hard cap — capacity is already enforced by the backend atomically.
    if (anyTierHasVariants) {
      setNewCopy(_defaultCopy());
      setEditingIdx(null);
      setAdding(true);
      return;
    }
    commit([...copies, _defaultCopy()]);
  };
  const cancelAdd = () => { setAdding(false); setNewCopy([]); setEditingIdx(null); };
  const commitAdd = () => {
    if (editingIdx !== null) {
      // Edit mode — replace at index, quantity unchanged.
      commit(copies.map((c, i) => i === editingIdx ? newCopy : c));
    } else {
      commit([...copies, newCopy]);
    }
    setAdding(false);
    setNewCopy([]);
    setEditingIdx(null);
  };
  const handleMinus = () => {
    if (copies.length === 0) return;
    commit(copies.slice(0, -1));
  };

  return (
    <div className={`relative bg-white rounded-2xl overflow-hidden shadow-sm border-2 flex flex-col ${isAvailable ? "border-[#0ea5e9]/20" : "border-slate-200"} ${!isAvailable && !blocked ? "opacity-70" : ""}`} data-testid={`package-${pkg.id}`} data-sold-out={isSoldOut ? "true" : "false"} data-status={st?.kind || "open"}>
      <div className="relative w-full aspect-square bg-slate-100 overflow-hidden">
        <div className={`w-full h-full ${blocked ? FADE_IMG : ""}`}>
        {pkg.banner_url ? (
          <img src={pkg.banner_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-slate-300 text-5xl">📦</div>
        )}
        </div>
        <StatusBand status={st} testid={`package-status-${pkg.id}`} />
        {!isAvailable && !st && (
          <span className="absolute top-2 left-2 text-xs bg-slate-700 text-white px-2 py-0.5 rounded-full" data-testid={`package-unavailable-${pkg.id}`}>Unavailable</span>
        )}
        {isAvailable && pkg.discount && pkg.discount.value > 0 && (
          <span className="absolute top-2 right-2 text-[10px] bg-amber-400 text-amber-900 font-bold px-2 py-0.5 rounded-full">
            {pkg.discount.type === "percent" ? `-${pkg.discount.value}%` : `-€${pkg.discount.value}`}
          </span>
        )}
      </div>
      <div className={`p-4 flex-1 flex flex-col ${blocked ? FADE_BODY : ""}`} aria-disabled={blocked || undefined}>
        <h4 className="font-bold text-[#0ea5e9]">📦 {pkg.name}</h4>
        {pkg.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{pkg.description}</p>}
        <p className="text-xs text-slate-500 mt-2">Includes: {tiers.map(t => t.name).join(" + ")}</p>

        {anyTierHasVariants && copies.length > 0 && (
          <div className="mt-3 space-y-1 max-h-32 overflow-y-auto pr-1" data-testid={`package-variants-${pkg.id}`}>
            {copies.map((copy, copyIdx) => {
              const chosen = copy.map(s => s.variant_name).filter(Boolean).join(" · ");
              return (
                <div key={copyIdx} className="flex items-center justify-between text-[11px] bg-slate-50 rounded-md px-2 py-1 gap-2" data-testid={`pkg-${pkg.id}-copy-${copyIdx}`}>
                  <span className="text-slate-600 truncate flex-1 min-w-0">
                    <span className="font-semibold text-slate-500">#{copyIdx + 1}</span>{chosen ? ` · ${chosen}` : ""}
                  </span>
                  <button
                    onClick={() => openEditAttendee(copyIdx)}
                    className="p-1 rounded hover:bg-slate-200 text-slate-400 hover:text-[#0ea5e9] shrink-0"
                    aria-label={`Edit attendee ${copyIdx + 1}`}
                    data-testid={`pkg-${pkg.id}-copy-${copyIdx}-edit`}
                  >
                    <Pencil className="w-3 h-3" />
                  </button>
                  <span className="font-semibold text-[#0ea5e9] shrink-0">€{copyEffectivePrices[copyIdx]?.effective.toFixed(2)}</span>
                </div>
              );
            })}
          </div>
        )}

        <div className="mt-2">
          <div className="flex items-baseline gap-2">
            {showStrike && (
              <span className="text-xs text-slate-400 line-through" data-testid={`pkg-strike-price-${pkg.id}`}>€{Number(staticBase).toFixed(2)}</span>
            )}
            <span className="text-xl font-extrabold text-[#0ea5e9]" data-testid={`pkg-base-price-${pkg.id}`}>€{Number(displayUnitPrice || 0).toFixed(2)}</span>
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5" data-testid={`pkg-final-price-note-${pkg.id}`}>final price is available at check out</p>
        </div>
        {blocked ? <StatusPill status={st} testid={`package-pill-${pkg.id}`} /> : <div className="flex items-center justify-end gap-2 mt-auto pt-3">
          <button
            onClick={handleMinus}
            disabled={copies.length === 0 || !isAvailable}
            className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 disabled:opacity-40 flex items-center justify-center"
            data-testid={`pkg-qty-minus-${pkg.id}`}
          ><Minus className="w-4 h-4" /></button>
          <span className="w-6 text-center font-bold" data-testid={`pkg-qty-${pkg.id}`}>{copies.length}</span>
          <button
            onClick={handlePlus}
            disabled={!isAvailable}
            className="w-8 h-8 rounded-full bg-[#0ea5e9] text-white hover:bg-[#0284c7] disabled:opacity-40 flex items-center justify-center"
            data-testid={`pkg-qty-plus-${pkg.id}`}
          ><Plus className="w-4 h-4" /></button>
        </div>}
      </div>

      {/* iter185: variant-picker modal — same UX as adding an attendee from
          the cart (see CartOverlay). Keeps the product-card details fully
          visible while the shopper picks options. */}
      {adding && (
        <div className="fixed inset-0 z-[70] flex items-end sm:items-center justify-center bg-black/50 p-3" data-testid={`pkg-add-attendee-modal-${pkg.id}`}>
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-5 max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between mb-2">
              <h3 className="font-extrabold text-[#1e3a5f]">
                {editingIdx !== null ? "Edit attendee" : "Add attendee to package"}
              </h3>
              <button onClick={cancelAdd} className="p-1 hover:bg-slate-100 rounded-full" aria-label="Close" data-testid={`pkg-add-attendee-close-${pkg.id}`}>
                <X className="w-5 h-5 text-slate-500" />
              </button>
            </div>
            <p className="text-sm font-semibold text-[#0ea5e9] mb-3">{pkg.name}</p>
            <div className="overflow-y-auto -mx-1 px-1 space-y-2 flex-1">
              <p className="text-[11px] font-semibold text-slate-500">
                Attendee #{editingIdx !== null ? editingIdx + 1 : copies.length + 1}
              </p>
              {tiers.map((t, occIdx) => {
                const variants = t.variants || [];
                if (variants.length === 0) {
                  return (
                    <div key={occIdx} className="flex items-center justify-between text-xs text-slate-500 px-1">
                      <span className="truncate">{t.name}</span>
                      <span>€{Number(t.effective_price || t.price || 0).toFixed(2)}</span>
                    </div>
                  );
                }
                return (
                  <div key={occIdx} className="flex items-center justify-between gap-2 py-1">
                    <label className="text-xs text-slate-600 truncate flex-1 min-w-0" htmlFor={`pkg-${pkg.id}-add-tier-${occIdx}`}>{t.name}</label>
                    <select
                      id={`pkg-${pkg.id}-add-tier-${occIdx}`}
                      className="text-xs bg-white border border-slate-200 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#0ea5e9]"
                      value={newCopy[occIdx]?.variant_id || variants[0].id}
                      onChange={(e) => setNewCopyVariant(occIdx, e.target.value)}
                      data-testid={`pkg-${pkg.id}-add-tier-${occIdx}-variant-select`}
                    >
                      {variants.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                    </select>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center gap-2 mt-4">
              <button onClick={cancelAdd} className="flex-1 py-2 rounded-lg border border-slate-300 text-slate-700 text-sm font-semibold hover:bg-slate-50" data-testid={`pkg-add-attendee-cancel-${pkg.id}`}>
                Cancel
              </button>
              <button onClick={commitAdd} className="flex-1 py-2 rounded-lg bg-[#0ea5e9] text-white text-sm font-bold hover:bg-[#0284c7]" data-testid={`pkg-add-attendee-save-${pkg.id}`}>
                {editingIdx !== null ? "Save changes" : "Add attendee"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
